import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  getInventoryItemTypeLabelKey,
  isInventoryItemType,
} from 'src/app/core/enums/inventory-item-type.enum';
import { InventoryItemChange } from 'src/app/core/models/inventory/inventory-item-change.interface';
import { InventoryItemComponentChangeInfo } from 'src/app/core/models/inventory/inventory-item-component-change-info.interface';
import {
  InventoryItemHistoryComponentDetailsVm,
  InventoryItemHistoryDiffDisplayValueVm,
  InventoryItemHistoryDiffRowVm,
  InventoryItemHistoryEntryVariant,
  InventoryItemHistoryEntryVm,
} from './inventory-item-history-panel.view-model';

@Component({
  selector: 'app-inventory-item-history-panel',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './inventory-item-history-panel.component.html',
  styleUrl: './inventory-item-history-panel.component.scss',
})
export class InventoryItemHistoryPanelComponent implements OnChanges {
  @Input() public isOpen = false;
  @Input() public itemTitle = '';
  @Input() public changes: InventoryItemChange[] = [];
  @Output() public readonly closed = new EventEmitter<void>();

  public historyEntries: InventoryItemHistoryEntryVm[] = [];

  private readonly translateService = inject(TranslateService);

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['changes']) {
      return;
    }

    this.historyEntries = this.changes.map((change) => this.buildEntry(change));
  }

  private buildEntry(change: InventoryItemChange): InventoryItemHistoryEntryVm {
    // Entity-lifecycle and component-mutation rows render dedicated messages and skip the
    // legacy diff list; everything else falls through to the diff-driven path so legacy
    // rows (eventType = null) keep producing the same output.
    const isLifecycleEvent = this.isLifecycleEvent(change.eventType);
    const diffRows = isLifecycleEvent ? [] : this.buildDiffRows(change);
    const variant = this.resolveEntryVariant(change);

    return {
      id: change.id,
      summaryKey: this.resolveSummaryKey(change),
      summaryParams: this.resolveSummaryParams(change),
      initiator: change.initiator,
      createdAt: change.createdAt,
      variant,
      dotClass: this.resolveEntryDotClass(variant),
      diffRows,
      componentDetails: this.resolveComponentDetails(change),
      sourceLabelKey: this.resolveSourceLabelKey(change.source),
    };
  }

  /**
   * Maps the backend-stamped origin discriminator to an i18n key under
   * COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES, reusing the keys already defined for the
   * identification tab. Legacy rows persisted before the source-tracking migration carry
   * null/undefined; anything unrecognized falls through to null so the template skips the
   * badge entirely instead of leaking a raw key.
   */
  private resolveSourceLabelKey(source: InventoryItemChange['source']): string | null {
    if (source === 'OrgLines') {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.ORGLINES';
    }
    if (source === 'BusinessCentral') {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.BUSINESS_CENTRAL';
    }
    return null;
  }

  private buildDiffRows(change: InventoryItemChange): InventoryItemHistoryDiffRowVm[] {
    return change.changes
      .map((diff) => {
        const labelKey = this.resolveFieldLabelKey(diff.path);
        if (!labelKey) {
          return null;
        }

        return {
          id: `${change.id}:${diff.path}`,
          labelKey,
          ...this.buildDiffRowValueVm(diff.path, diff.oldValue, diff.newValue),
        };
      })
      .filter((entry): entry is InventoryItemHistoryDiffRowVm => entry !== null);
  }

  private isLifecycleEvent(eventType: InventoryItemChange['eventType']): boolean {
    return (
      eventType === 'Created'
      || eventType === 'ComponentAdded'
      || eventType === 'ComponentUpdated'
      || eventType === 'ComponentRemoved'
    );
  }

  private resolveEntryDotClass(variant: InventoryItemHistoryEntryVariant): string {
    const dotClasses: Record<InventoryItemHistoryEntryVariant, string> = {
      archived: 'inventory-item-history-panel__item-dot--archived',
      restored: 'inventory-item-history-panel__item-dot--restored',
      updated: 'inventory-item-history-panel__item-dot--updated',
      created: 'inventory-item-history-panel__item-dot--created',
      'component-added': 'inventory-item-history-panel__item-dot--component-added',
      'component-updated': 'inventory-item-history-panel__item-dot--component-updated',
      'component-removed': 'inventory-item-history-panel__item-dot--component-removed',
    };

    return dotClasses[variant];
  }

  private resolveSummaryKey(change: InventoryItemChange): string {
    // Branch on the backend EventType discriminator first so entity-lifecycle and
    // component-mutation rows render dedicated messages without walking the diff. Legacy
    // rows persisted before the 2026-05-20 migration carry eventType = null and fall
    // through to the existing diff-based resolver to preserve their output.
    if (change.eventType === 'Created') {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.CREATED';
    }
    if (change.eventType === 'ComponentAdded') {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.COMPONENT_ADDED';
    }
    if (change.eventType === 'ComponentUpdated') {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.COMPONENT_UPDATED';
    }
    if (change.eventType === 'ComponentRemoved') {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.COMPONENT_REMOVED';
    }

    const changedPaths = change.changes.map((entry) => entry.path.toLowerCase());

    if (changedPaths.includes('isArchived'.toLowerCase())) {
      const archiveChange = change.changes.find((entry) => entry.path.toLowerCase() === 'isArchived'.toLowerCase());
      return archiveChange?.newValue === true
        ? 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.ARCHIVED'
        : 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.UNARCHIVED';
    }

    if (changedPaths.some((path) => path.startsWith('unitPricing/') || path.includes('unitPrice') || path.includes('unitCost'))) {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.PRICE_AND_COST';
    }

    if (
      changedPaths.some((path) =>
        ['stock', 'warehouse', 'baseUnitOfMeasureId', 'length', 'width', 'height', 'weight', 'cubage'].includes(path.split('/')[0]))
    ) {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.LOGISTICS';
    }

    if (changedPaths.some((path) => ['shortDescription', 'fullDescription'].includes(path.split('/')[0]))) {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.DESCRIPTION';
    }

    if (changedPaths.some((path) => ['number', 'gtin'].includes(path.split('/')[0]))) {
      return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.IDENTIFICATION';
    }

    return 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.EVENTS.DETAILS';
  }

  private resolveEntryVariant(change: InventoryItemChange): InventoryItemHistoryEntryVariant {
    if (change.eventType === 'Created') {
      return 'created';
    }
    if (change.eventType === 'ComponentAdded') {
      return 'component-added';
    }
    if (change.eventType === 'ComponentUpdated') {
      return 'component-updated';
    }
    if (change.eventType === 'ComponentRemoved') {
      return 'component-removed';
    }

    const archiveChange = change.changes.find((entry) => entry.path.toLowerCase() === 'isArchived'.toLowerCase());
    if (archiveChange) {
      return archiveChange.newValue === true ? 'archived' : 'restored';
    }

    return 'updated';
  }

  private resolveSummaryParams(change: InventoryItemChange): Record<string, string> | undefined {
    if (
      change.eventType !== 'ComponentAdded'
      && change.eventType !== 'ComponentUpdated'
      && change.eventType !== 'ComponentRemoved'
    ) {
      return undefined;
    }

    return { name: this.resolveComponentNameForMessage(change.component) };
  }

  /**
   * Resolves a single human-readable label for a component-mutation message. The backend
   * snapshots both displayName and number on the audit row at write time so the message
   * stays stable; the FE just picks the best one to show.
   *   - displayName present                -> "Wheel"
   *   - displayName missing, number present -> "ITM-0042"
   *   - both missing                       -> translated generic "Component" word.
   * Generic fallback is resolved via TranslateService.instant up front — ngx-translate does
   * not auto-translate parameter values handed to the {{name}} placeholder, so returning a
   * raw key would leak it into the rendered string.
   */
  private resolveComponentNameForMessage(component: InventoryItemComponentChangeInfo | null | undefined): string {
    if (component?.displayName) {
      return component.displayName;
    }
    if (component?.number) {
      return component.number;
    }
    return this.translateService.instant('COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.GENERIC_COMPONENT');
  }

  private resolveComponentDetails(
    change: InventoryItemChange,
  ): InventoryItemHistoryComponentDetailsVm | undefined {
    if (
      change.eventType !== 'ComponentAdded'
      && change.eventType !== 'ComponentUpdated'
      && change.eventType !== 'ComponentRemoved'
    ) {
      return undefined;
    }

    const component = change.component;
    const newQuantity = component?.quantity ?? null;
    // For ComponentUpdated the backend persists the previous quantity in the diff payload
    // (path "components/{guid}/quantity", oldValue = previousQuantity). Surface "from X
    // to Y" when both sides are present so the row reads naturally.
    const previousQuantity = this.extractPreviousQuantity(change);

    return {
      hasQuantity: newQuantity !== null && newQuantity !== undefined,
      quantity: newQuantity ?? undefined,
      hasPreviousQuantity:
        change.eventType === 'ComponentUpdated'
        && previousQuantity !== null
        && previousQuantity !== undefined,
      previousQuantity: previousQuantity ?? undefined,
    };
  }

  private extractPreviousQuantity(change: InventoryItemChange): number | null {
    const quantityDiff = change.changes.find((diff) => {
      const normalized = diff.path.replace(/^\//, '').toLowerCase();
      return normalized.startsWith('components/') && normalized.endsWith('/quantity');
    });
    if (!quantityDiff) {
      return null;
    }
    const value = quantityDiff.oldValue;
    return typeof value === 'number' ? value : null;
  }

  private resolveFieldLabelKey(path: string): string | null {
    const normalizedPath = path.replace(/^\//, '');
    const segments = normalizedPath.split('/');
    const rootKey = segments[0];
    const unitPricingField = segments.length >= 3 && rootKey === 'unitPricing' ? segments[2] : null;
    const fieldKey = unitPricingField ?? rootKey;

    const labels: Record<string, string> = {
      displayName: 'COMPONENTS.GOODS.FIELDS.NAME',
      displayName2: 'COMPONENTS.GOODS.FIELDS.ADDITIONAL_NAME',
      number: 'COMPONENTS.GOODS.FIELDS.SKU',
      gtin: 'COMPONENTS.GOODS.FIELDS.GTIN',
      itemType: 'COMPONENTS.GOODS.FIELDS.TYPE',
      status: 'COMPONENTS.GOODS.FIELDS.STATUS',
      shortDescription: 'COMPONENTS.GOODS.FIELDS.SHORT_DESCRIPTION',
      fullDescription: 'COMPONENTS.GOODS.FIELDS.FULL_DESCRIPTION',
      manufacturer: 'COMPONENTS.GOODS.FIELDS.MANUFACTURER',
      model: 'COMPONENTS.GOODS.FIELDS.MODEL',
      stock: 'COMPONENTS.GOODS.FIELDS.STOCK',
      baseUnitOfMeasureId: 'COMPONENTS.GOODS.FIELDS.UNIT',
      warehouse: 'COMPONENTS.GOODS.FIELDS.WAREHOUSE',
      length: 'COMPONENTS.GOODS.FIELDS.LENGTH',
      width: 'COMPONENTS.GOODS.FIELDS.WIDTH',
      height: 'COMPONENTS.GOODS.FIELDS.HEIGHT',
      weight: 'COMPONENTS.GOODS.FIELDS.WEIGHT',
      cubage: 'COMPONENTS.GOODS.FIELDS.CUBAGE',
      unitPrice: 'COMPONENTS.GOODS.FIELDS.SALES_PRICE',
      unitCost: 'COMPONENTS.GOODS.FIELDS.COST',
      currencyCode: 'COMPONENTS.GOODS.FIELDS.CURRENCY',
      priceIncludesTax: 'COMPONENTS.GOODS.FIELDS.PRICE_INCLUDES_VAT',
      qtyPerUnitOfMeasure: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_QTY',
      isArchived: 'BUTTONS.ARCHIVE',
    };

    return labels[fieldKey] ?? null;
  }

  private buildDiffRowValueVm(
    path: string,
    oldValue: unknown,
    newValue: unknown,
  ): Pick<InventoryItemHistoryDiffRowVm, 'showOldValue' | 'oldValue' | 'newValue'> {
    const formattedOldValue = this.formatDiffValue(path, oldValue);
    const formattedNewValue = this.formatDiffValue(path, newValue);
    const showOldValue = oldValue !== null && oldValue !== undefined && oldValue !== '';

    return {
      showOldValue,
      oldValue: showOldValue ? formattedOldValue : undefined,
      newValue: formattedNewValue,
    };
  }

  private formatDiffValue(path: string, value: unknown): InventoryItemHistoryDiffDisplayValueVm {
    if (value === null || value === undefined || value === '') {
      return {
        text: 'COMMON.EMPTY_VALUE',
        translationKey: 'COMMON.EMPTY_VALUE',
      };
    }

    if (typeof value === 'boolean') {
      return {
        text: value ? 'COMMON.YES' : 'COMMON.NO',
        translationKey: value ? 'COMMON.YES' : 'COMMON.NO',
      };
    }

    // Localize known enum values for itemType diffs (Product/Service/Hybrid). Legacy
    // snapshot rows may carry the old free-text labels ("Inventory", "Item", "Non-Inventory");
    // for those we fall through to the raw string so the historical diff stays readable.
    if (this.isItemTypePath(path) && isInventoryItemType(value)) {
      const translationKey = getInventoryItemTypeLabelKey(value);
      return { text: translationKey, translationKey };
    }

    return { text: `${value}` };
  }

  private isItemTypePath(path: string): boolean {
    return path.replace(/^\//, '').split('/')[0] === 'itemType';
  }
}
